import React, { useState } from 'react';
import type { PcrSubmission } from '../../../services/pcrService';
import type { WorkIdActionsMenuItem } from '../../work-id/components/WorkIdActionsMenu';
import WorkIdActionsMenu from '../../work-id/components/WorkIdActionsMenu';

export interface PcrRowActionsProps {
  row: PcrSubmission;
  pcrReplyUiEnabled?: boolean;
  canPcrReply?: boolean;
  canUpdateStatus?: boolean;
  /** Move New → Internal Review (pcr:internal_review or submission:pcr_update | workId:create). */
  canMoveToInternalReview?: boolean;
  onMoveToInternalReview?: (row: PcrSubmission) => void;
  /** Move Internal Review → Finalized (pcr:finalize). */
  canMoveToFinalize?: boolean;
  onMoveToFinalize?: (row: PcrSubmission) => void;
  statusTransitioningId?: string | null;
  onReply?: (row: PcrSubmission) => void;
  onEdit?: (row: PcrSubmission) => void;
  onOpenDetail: (row: PcrSubmission) => void;
  canOpenNotesHistory?: boolean;
  onOpenNotesHistory?: (row: PcrSubmission) => void;
  /** Manual PCR → Work ID (after field assessment). */
  canPromoteToWorkId?: boolean;
  onPromoteToWorkId?: (row: PcrSubmission) => void;
  /** Finalize PCR with no maintenance work. */
  canCloseWithoutWork?: boolean;
  onCloseWithoutWork?: (row: PcrSubmission) => void;
  /** Open Work IDs list filtered to the linked id. */
  onOpenLinkedWorkId?: (row: PcrSubmission) => void;
  /** Full audit PDF (history, emails, Work ID, closure) — same permission gate as Notes & history. */
  canExportAuditReport?: boolean;
  onExportAuditReport?: (row: PcrSubmission) => void;
  auditReportExportingId?: number | null;
}

/**
 * Same trigger + portal dropdown pattern as Work ID actions (WorkIdActions + WorkIdActionsMenu).
 */
const PcrRowActions: React.FC<PcrRowActionsProps> = ({
  row,
  pcrReplyUiEnabled,
  canPcrReply,
  canUpdateStatus,
  canMoveToInternalReview,
  onMoveToInternalReview,
  canMoveToFinalize,
  onMoveToFinalize,
  statusTransitioningId,
  onReply,
  onEdit,
  onOpenDetail,
  canOpenNotesHistory,
  onOpenNotesHistory,
  canPromoteToWorkId,
  onPromoteToWorkId,
  canCloseWithoutWork,
  onCloseWithoutWork,
  onOpenLinkedWorkId,
  canExportAuditReport,
  onExportAuditReport,
  auditReportExportingId,
}) => {
  const [menuOpen, setMenuOpen] = useState<{ x: number; y: number } | null>(null);
  const hasEmail = Boolean((row.user_email || '').trim());
  const status = row.status || 'new';
  const transitioning = statusTransitioningId != null && statusTransitioningId === String(row.id);
  const showMoveToInternalReview =
    canMoveToInternalReview && onMoveToInternalReview && status === 'new';
  const showMoveToFinalize =
    canMoveToFinalize && onMoveToFinalize && status === 'internal_review' && !row.workIdId;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    setMenuOpen({ x: rect.right - 220, y: rect.bottom + 4 });
  };

  const buildMenuItems = (): WorkIdActionsMenuItem[] => {
    const items: WorkIdActionsMenuItem[] = [];
    items.push({ label: 'View details', onClick: () => onOpenDetail(row) });
    if (onEdit) {
      items.push({ label: 'Edit', onClick: () => onEdit(row) });
    }
    if (showMoveToInternalReview) {
      items.push({
        label: transitioning ? 'Moving to Internal Review…' : 'Move to Internal Review',
        onClick: () => onMoveToInternalReview!(row),
        disabled: transitioning,
        title: 'Requires pcr:internal_review',
      });
    }
    if (showMoveToFinalize) {
      items.push({
        label: transitioning ? 'Moving to Finalized…' : 'Move to Finalized',
        onClick: () => onMoveToFinalize!(row),
        disabled: transitioning,
        title: 'Requires pcr:finalize',
      });
    }
    if (canOpenNotesHistory && onOpenNotesHistory) {
      items.push({ label: 'Notes & history', onClick: () => onOpenNotesHistory(row) });
    }
    if (canExportAuditReport && onExportAuditReport) {
      const busy = auditReportExportingId != null && auditReportExportingId === Number(row.id);
      items.push({
        label: busy ? 'Exporting...' : 'Export to PDF',
        onClick: () => onExportAuditReport(row),
        disabled: busy,
        title: 'PCR Export: submission summary, timelines, notes, outbound emails, Work ID linkage (same layout style as Work ID export)',
      });
    }
    if (onReply) {
      const disabledByPermission = !canPcrReply;
      const disabledByFlag = !pcrReplyUiEnabled;
      items.push({
        label: 'Send response to requester',
        onClick: () => onReply(row),
        disabled: disabledByPermission || disabledByFlag || !hasEmail,
        title: disabledByPermission
          ? 'Permission submission:pcr_reply is required'
          : disabledByFlag
          ? 'Reply UI is disabled by environment configuration'
          : hasEmail
            ? 'Compose email to requester'
            : 'Submitter email is missing',
      });
    }
    if (row.workIdId && onOpenLinkedWorkId) {
      items.push({
        label: 'Open linked Work ID',
        onClick: () => onOpenLinkedWorkId(row),
        title: 'View this PCR’s Work ID in Work ID Management',
      });
    }
    if (canPromoteToWorkId && onPromoteToWorkId) {
      items.push({
        label: 'Convert to Work ID…',
        onClick: () => onPromoteToWorkId(row),
        disabled: Boolean(row.workIdId) || row.status === 'finalized',
        title:
          row.workIdId || row.status === 'finalized'
            ? 'Already finalized or linked to a Work ID'
            : 'Create a Work ID from this PCR after assessment',
      });
    }
    if (canCloseWithoutWork && onCloseWithoutWork) {
      items.push({
        label: 'Close without Work ID…',
        onClick: () => onCloseWithoutWork(row),
        disabled: Boolean(row.workIdId) || row.status === 'finalized',
        title:
          row.workIdId || row.status === 'finalized'
            ? 'Already finalized or linked to a Work ID'
            : 'Finalize this PCR with no Work ID (e.g. no action required)',
      });
    }
    return items;
  };

  return (
    <>
      <button
        className="tw-bg-transparent tw-border-0 tw-p-0 tw-text-content-secondary"
        type="button"
        onClick={handleClick}
        aria-expanded={!!menuOpen}
        aria-haspopup="true"
        title="Actions"
      >
        <i className="bi bi-three-dots-vertical" aria-hidden />
      </button>
      {menuOpen && (
        <WorkIdActionsMenu
          left={menuOpen.x}
          top={menuOpen.y}
          onClose={() => setMenuOpen(null)}
          items={buildMenuItems()}
        />
      )}
    </>
  );
};

export default PcrRowActions;
